import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  AfterViewInit,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { statesOptions } from 'src/app/data/states';
import {
  BasicInfo,
  SignupService,
} from 'src/app/services/signup/signup.service';

import fakerbr from 'faker-br';
import { MaskPipe } from 'ngx-mask';
import { AuthService } from 'src/app/services/auth/auth.service';
import DDIOptions from 'src/app/data/ddi';
import { ApiService } from 'src/app/services/api/api.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import Utils, {
  GetHelper,
  payloadsEqual,
  UtilsValidators,
} from 'src/app/helpers/utils';
import employeesOptions from 'src/app/data/employeesOptions';
import { StorageService } from 'src/app/services/storage/storage.service';

type EmailProps = {
  label: string;
  primary: boolean;
};

/** Beneficiário de fundo exclusivo (API exclusive-fund-beneficiaries) */
export type ExclusiveFundBeneficiary = {
  name: string;
  document: string;
  is_pep: boolean;
};

@Component({
  selector: 'app-signup-unified-company-complementary-general',
  templateUrl: './signup-unified-company-complementary-general.component.html',
  styleUrls: ['./signup-unified-company-complementary-general.component.scss'],
})
export class SignupUnifiedCompanyComplementaryGeneralComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  DDIOptions = DDIOptions;

  @Input() userRole: string;

  form = this.formBuilder.group(
    {
      cnpj: [null, [Validators.required, UtilsValidators.cnpj]],
      name: [null, [Validators.required, UtilsValidators.notOnlyWhitespace]],
      brand_name: [
        null,
        [Validators.required, UtilsValidators.notOnlyWhitespace],
      ],

      foundation_date: [null, []],
      branch: [null, [Validators.required]],
      framework: [null, [Validators.required]],
      agent: [null, []],

      municipal_registration: [null, [UtilsValidators.notOnlyWhitespace]],
      state_registration: [null, [UtilsValidators.notOnlyWhitespace]],
      free_state_registration: [false, []],

      nire: [null, [UtilsValidators.notOnlyWhitespace]],
      giin: [null, []],
      // joint_obligation: [false, []],

      billet_emitter: [false, []],
      nfe_emitter: [false, []],
      duplicate_emitter: [false, []],
      have_judicial_recovery: [false, []],

      contract_type: [null, [Validators.required]],

      cellphone_ddi: ['55', []],
      cellphone_number: [
        null,
        [
          Validators.required,
          Utils.validateWhenNotEmpty(UtilsValidators.celular),
        ],
      ],
      telephone_ddi: ['55', []],
      telephone_number: [
        null,
        [Utils.validateWhenNotEmpty(UtilsValidators.telefoneFixo)],
      ],

      contact_email: [
        null,
        [Validators.required, Validators.email, UtilsValidators.emailValidator],
      ],
      secondary_email: [
        null,
        [Validators.email, UtilsValidators.emailValidator],
      ],
      primary_contact: [false, []],
      site: [
        '',
        [
          UtilsValidators.notOnlyWhitespace,
          UtilsValidators.noSpaces,
          UtilsValidators.urlValidator,
        ],
      ],

      send_info: [true, [Validators.requiredTrue]],

      cnae: [null, [Validators.required]],

      company_size: [null, [Validators.required]],

      // interest_fund: [null, []],

      judicial_recovery: [false, []],
      judicial_recovery_date: [null, []],

      pep_relationship: [false, []],
      pep_relationship_document: [null, []],
      pep_relationship_name: [null, []],
      pep_bond: [null, []],

      yearly_income: [0, []],
      number_employees: [null, []],
      capital: [0, []],
      authorization_bacen_queries: [false, []],
      /** Cedente: integrante do SFN (Sistema Financeiro Nacional) */
      sfn: [false, []],
      relationship_with_other_institutions: [false, []],
      relationship_institution: [null, []],
      third_party: [false, []],
      third_party_document: [null, []],
      third_party_name: [null, []],

      exclusive_fund: [false, []],
      beneficiary_name: [null, []],
      beneficiary_document: [null, []],
      beneficiary_estrangeiro: [false, []],
      beneficiary_is_pep: [false, []],
    },
    { updateOn: 'change' },
  );

  columns: TableColumn<EmailProps>[] = [
    {
      name: 'Email',
      cell: (row) => String(row.label),
    },
    {
      name: 'contato principal',
      cell: (row) => (row.primary ? 'Sim' : 'Não'),
    },
  ];

  emailArr: EmailProps[] = [];

  /** Lista de beneficiários de fundo exclusivo (API v3 exclusive-fund-beneficiaries) */
  exclusiveFundBeneficiaries: ExclusiveFundBeneficiary[] = [];

  /** Controla exibição do formulário de beneficiário (primeiro ou “adicionar outro”). */
  showBeneficiaryForm: boolean = false;

  beneficiaryColumns: TableColumn<ExclusiveFundBeneficiary>[] = [
    { name: 'Nome/Razão Social', cell: (row) => row.name },
    { name: 'Documento', cell: (row) => row.document },
    { name: 'PEP', cell: (row) => (row.is_pep ? 'Sim' : 'Não') },
  ];

  states = statesOptions;

  gender: SelectItem[] = [
    { label: 'Masculino', value: '1' },
    { label: 'Feminino', value: '2' },
  ];

  roles: SelectItem[] = [
    { label: 'Investidor', value: '1' },
    { label: 'Empresa', value: '2' },
  ];

  items: SelectItem[] = [
    { value: '1', label: 'Sim' },
    { value: '2', label: 'Não' },
  ];

  contractTypeItems: SelectItem[] = [
    { value: 'physical', label: 'Físico' },
    { value: 'digital', label: 'Digital' },
  ];

  employeesOptions = employeesOptions;

  today = Utils.todayString();

  haveBasicInfo: boolean = false;

  invitedInfo: BasicInfo = {
    document: null,
    name: null,
    email: null,
  };

  data: any = null;

  /** Snapshot do payload após o GET; usado para evitar PATCH/PUT quando nada mudou. */
  private initialPayload: Record<string, any> | null = null;

  /** Snapshot da lista de beneficiários para detectar alterações. */
  private initialExclusiveFundBeneficiaries = '';

  foundPerson = false;

  event: any;

  fundOptions: SelectItem[] = [];
  loadingContent = false;
  choicesLoaded = false;
  apisLoaded = false;
  legacyVersionChecked = false;

  dataDocument: any = null;
  agentOptions: SelectItem[] = [];

  get isFund() {
    return this.signupService.getIsFund();
  }

  constructor(
    private formBuilder: FormBuilder,
    public signupService: SignupService,
    private maskPipe: MaskPipe,
    public authService: AuthService,
    private api: ApiService,
    private toast: ToastService,
    private storageService: StorageService,
    private route: ActivatedRoute,
  ) {}

  ngAfterViewInit(): void {
    // Lifecycle hook - view inicializada
  }

  ngOnDestroy(): void {
    this.event.unsubscribe();
  }

  ngOnInit(): void {
    this.setFieldsByRole();
    this.applySiteValidatorsByFund();

    // Iniciar loading spinner
    this.loadingContent = true;
    // Backend já cria versão legacy automaticamente — não há chamada async; definir cedo evita race com Promise.all
    this.legacyVersionChecked = true;

    // Não precisa mais chamar checkAndCreateLegacyVersion - o backend já cria versão 1 automaticamente

    // Chamar API de fundos em background (não bloqueia o loading)
    this.getFunds();

    // Verificar se os choices já estão carregados
    this.checkChoicesAndDisableLoading();

    // Se não estiverem carregados, aguardar o evento
    this.signupService.choicesFetchedEvent.subscribe(() => {
      this.checkChoicesAndDisableLoading();
    });

    // Chamar todas as APIs essenciais em paralelo e aguardar todas terminarem
    Promise.all([
      this.getDataAndFillForm(),
      this.getEmployeeOptions(),
      this.getAgents(),
    ]).finally(() => {
      this.apisLoaded = true;
      this.legacyVersionChecked = true;
      this.checkChoicesAndDisableLoading();
      this.applySiteValidatorsByFund();
    });

    if (this.userRole === 'provider') {
      this.form.controls.agent.setValidators([Validators.required]);
    }

    this.form.controls.cellphone_number.valueChanges
      .pipe(distinctUntilChanged(), debounceTime(150))
      .subscribe((value) => {
        if (this.signupService.getIsFund() || this.userRole === 'provider') {
          if (value) {
            this.form.controls.telephone_number.setValidators([
              Validators.required,
              Utils.validateWhenNotEmpty(UtilsValidators.telefoneFixo),
            ]);
          } else {
            this.form.controls.telephone_number.setValidators([
              Validators.required,
              Utils.validateWhenNotEmpty(UtilsValidators.telefoneFixo),
            ]);
          }
        }
      });

    this.form.controls.telephone_number.valueChanges
      .pipe(distinctUntilChanged(), debounceTime(150))
      .subscribe((value) => {
        if (this.signupService.getIsFund() || this.userRole === 'provider') {
          if (value) {
            this.form.controls.cellphone_number.setValidators([
              Validators.required,
              Utils.validateWhenNotEmpty(UtilsValidators.celular),
            ]);
          } else {
            this.form.controls.cellphone_number.setValidators([
              Utils.validateWhenNotEmpty(UtilsValidators.celular),
              Validators.required,
            ]);
          }
          this.form.controls.cellphone_number.updateValueAndValidity();
        }

        // Sempre validar telefone e marcar como touched se houver erro
        if (value) {
          this.form.controls.telephone_number.updateValueAndValidity();
          if (this.form.controls.telephone_number.invalid) {
            this.form.controls.telephone_number.markAsTouched();
          }
        }
      });

    this.form.controls.cnpj.valueChanges.subscribe(async (value) => {
      if (!value) {
        this.foundPerson = false;
      }
      const document = Utils.normalizeDocumentForApi(value);
      if (Utils.isDocumentComplete(value)) {
        if (
          !(this.invitedInfo && this.invitedInfo.document) &&
          document !== this.dataDocument
        ) {
          this.checkDocument(value, 'register');
        }

        // const { name, foundPerson } = await this.api.getPersonName(value);
        // this.foundPerson = foundPerson;

        // if (name) this.form.controls.name.setValue(name);
      }
    });

    this.form.statusChanges.subscribe((status) => {
      const isValid = this.isFormValid();
      this.signupService.setCompanyFormStatus('complementaryGeneral', isValid);

      if (!isValid) {
        console.error(Utils.getErrors(this.form));
      }
    });

    this.event = this.signupService.sendComplementaryCompanyData.subscribe(
      (value) => {
        if (value === 'complementaryGeneral') {
          this.sendData();
        }
      },
    );

    this.form.controls.foundation_date.valueChanges
      .pipe(distinctUntilChanged((a: any, b: any) => a === b))
      .subscribe((value) => {
        if (!value) {
          this.form.controls.foundation_date.reset();
          this.form.controls.foundation_date.setValidators([]);
        } else {
          this.form.controls.foundation_date.setValidators([
            Utils.validateMaxMinToday,
            Utils.validateMinDate,
          ]);
        }

        this.form.controls.foundation_date.updateValueAndValidity();
      });

    this.form.valueChanges.subscribe((value) => {
      const isValid = this.isFormValid();
      this.signupService.setCompanyFormStatus('complementaryGeneral', isValid);
    });

    // Converte email para minúsculas em tempo real (mesmo comportamento do cadastro básico)
    this.form.controls.contact_email.valueChanges.subscribe((value) => {
      if (value && typeof value === 'string' && value !== value.toLowerCase()) {
        this.form.controls.contact_email.setValue(value.toLowerCase(), {
          emitEvent: false,
        });
      }
    });

    this.form.controls.secondary_email.valueChanges.subscribe((value) => {
      if (value && typeof value === 'string' && value !== value.toLowerCase()) {
        this.form.controls.secondary_email.setValue(value.toLowerCase(), {
          emitEvent: false,
        });
      }
    });

    // Função para preencher campos do basicInfo
    const fillBasicInfo = () => {
      if (this.signupService.basicInfo !== null && !this.haveBasicInfo) {
        this.haveBasicInfo = true;

        this.invitedInfo = this.signupService.basicInfo;

        this.form.patchValue({
          cnpj: this.invitedInfo.document ?? null,
          name: this.invitedInfo.name ?? null,
          contact_email: this.invitedInfo.email,
        });

        return true;
      }
      return false;
    };

    // Verificar imediatamente (defer para não interromper o restante do ngOnInit em caso de erro no form)
    setTimeout(() => {
      fillBasicInfo();
      if (!this.haveBasicInfo) {
        setTimeout(() => fillBasicInfo(), 500);
      }
    }, 0);

    const newUserDataStorage = this.storageService.getNewUserData();

    if (newUserDataStorage) {
      this.invitedInfo.document = newUserDataStorage.document;
      this.form.patchValue({
        cnpj: newUserDataStorage.document,
      });
    }

    this.form.controls.judicial_recovery.valueChanges.subscribe((value) => {
      if (value) {
        this.form.controls.judicial_recovery_date.setValidators([
          Validators.required,
          Utils.validateMaxMinToday,
        ]);
        this.form.controls.judicial_recovery_date.setValue(null);
        // this.form.controls.joint_obligation.setValue(false);
      } else {
        this.form.controls.judicial_recovery_date.setValidators([]);
        this.form.controls.judicial_recovery_date.setValue(null);
      }

      this.form.controls.judicial_recovery_date.updateValueAndValidity();
      //   this.form.controls.joint_obligation.updateValueAndValidity();
    });

    this.form.controls.free_state_registration.valueChanges.subscribe(
      (value) => {
        if (value) {
          this.form.controls.state_registration.setValue(null);
        }

        this.form.controls.state_registration.updateValueAndValidity();
      },
    );

    this.form.controls.exclusive_fund.valueChanges.subscribe((value) => {
      if (value) {
        // Fundo exclusivo marcado: se ainda não houver beneficiário, abre o formulário e aplica validações.
        this.showBeneficiaryForm = this.exclusiveFundBeneficiaries.length === 0;
        if (this.showBeneficiaryForm) {
          this.enableBeneficiaryValidators();
        } else {
          this.disableBeneficiaryValidators();
        }
      } else {
        // Fundo exclusivo desmarcado: fecha o formulário e limpa campos auxiliares e validações.
        this.showBeneficiaryForm = false;
        this.disableBeneficiaryValidators();
        this.form.controls.beneficiary_estrangeiro.setValue(false, {
          emitEvent: false,
        });
        this.form.controls.beneficiary_is_pep.setValue(false, {
          emitEvent: false,
        });
        this.form.controls.beneficiary_name.setValue(null, {
          emitEvent: false,
        });
        this.form.controls.beneficiary_document.setValue(null, {
          emitEvent: false,
        });
      }
    });

    this.form.controls.beneficiary_estrangeiro.valueChanges.subscribe(() => {
      if (!this.form.controls.exclusive_fund.value) return;
      // Sempre limpar o documento ao trocar o tipo
      this.form.controls.beneficiary_document.setValue(null, {
        emitEvent: false,
      });
      // Reaplicar validações de documento quando o formulário estiver aberto
      if (this.showBeneficiaryForm) {
        this.applyBeneficiaryDocumentValidators();
        this.form.controls.beneficiary_document.updateValueAndValidity({
          emitEvent: false,
        });
      }
    });

    this.signupService.fillFormEvent.subscribe(() => {
      // Verificar basicInfo novamente quando fillFormEvent for emitido
      if (this.signupService.basicInfo !== null && !this.haveBasicInfo) {
        this.haveBasicInfo = true;
        this.invitedInfo = this.signupService.basicInfo;
        this.form.patchValue({
          cnpj: this.invitedInfo.document ?? null,
          name: this.invitedInfo.name ?? null,
          contact_email: this.invitedInfo.email,
        });
      }

      const relationshipWithOtherInstitutions = false;
      const pepRelationship = false;
      const thirdParty = fakerbr.random.boolean();

      const fakeDomain = fakerbr.internet.domainName();

      this.form.patchValue({
        cnpj: this.invitedInfo.document
          ? this.form.controls.cnpj.value
          : Utils.formatDocumentForDisplay(fakerbr.br.cnpj()),
        name: this.invitedInfo.name
          ? this.form.controls.name.value
          : fakerbr.company.companyName(),
        brand_name: fakerbr.company.companyName(),

        contact_email: this.invitedInfo.email
          ? this.form.controls.contact_email.value
          : `contato@${fakeDomain}`,

        foundation_date: fakerbr.date.past(20).toLocaleDateString('pt-BR', {
          timeZone: 'UTC',
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }),
        branch: String(fakerbr.random.number({ min: 1, max: 3 })),
        framework: String(fakerbr.random.number({ min: 1, max: 4 })),

        municipalRegistration: fakerbr.random.number({
          min: 10000000,
          max: 99999999,
        }),
        stateRegistration: fakerbr.random.number({
          min: 10000000,
          max: 99999999,
        }),

        nire: fakerbr.random.number({
          min: 10000000,
          max: 99999999,
        }),
        giin: fakerbr.random.number({
          min: 10000000,
          max: 99999999,
        }),

        cellphone_ddi: '55',
        cellphone_number: this.maskPipe.transform(
          '139' +
            String(fakerbr.random.number({ min: 11111111, max: 99999999 })),
          '(00) 00000-0000',
        ),
        telephone_ddi: '55',
        telephone_number: this.maskPipe.transform(
          '139' + String(fakerbr.random.number({ min: 1111111, max: 9999999 })),
          '(00) 0000-0000',
        ),
        // joint_obligation: false,

        site: `https://${fakeDomain}`,

        send_info: true,

        contract_type: 'digital',

        cnae: this.signupService.signupChoices.cnaes[
          fakerbr.random.number({ min: 1, max: 672 })
        ].value,

        company_size: String(fakerbr.random.number({ min: 1, max: 3 })),
        number_employees: 'one_to_ten',

        judicial_recovery_date: null,
      });

      if (this.userRole !== 'provider') {
        this.form.patchValue({
          thirdParty: thirdParty,
          thirdPartyDocument: thirdParty
            ? this.maskPipe.transform(fakerbr.br.cpf(), '000.000.000-00')
            : null,
          thirdPartyName: thirdParty ? fakerbr.name.findName() : null,
          pep_relationship: pepRelationship,
          pep_relationship_document: pepRelationship
            ? this.maskPipe.transform(fakerbr.br.cpf(), '000.000.000-00')
            : null,
          pep_relationship_name: pepRelationship
            ? fakerbr.name.findName()
            : null,
          pepBond: pepRelationship ? 'Sócio' : null,
          yearly_income: fakerbr.random.number({ min: 500000, max: 5000000 }),
          capital: fakerbr.random.number({ min: 50000000, max: 500000000 }),
          relationship_with_other_institutions:
            relationshipWithOtherInstitutions,
          relationship_institution: relationshipWithOtherInstitutions
            ? fakerbr.company.companyName()
            : '',
          authorization_bacen_queries: true,
        });
      }
    });

    this.form.controls.pep_relationship.valueChanges.subscribe(() =>
      this.verifyPepRelationshipFields(),
    );

    if (this.userRole !== 'provider') {
      this.form.controls.third_party.valueChanges.subscribe(() =>
        this.thirdPartyFields(),
      );
    }

    // Inicializar legacyVersionChecked como true (não precisa mais chamar API)
    this.legacyVersionChecked = true;
    this.checkChoicesAndDisableLoading();
  }

  checkChoicesAndDisableLoading() {
    const choices = this.signupService.choices();

    // Garantir que sempre retorne boolean (true ou false), nunca undefined
    const hasFrameworks = !!(
      choices &&
      choices.frameworks &&
      choices.frameworks.length > 0
    );
    const hasBranchChoices = !!(
      choices &&
      choices.branch_choices &&
      choices.branch_choices.length > 0
    );

    // Verificar se frameworks existe no objeto choices (mesmo que vazio)
    const frameworksExists = choices && 'frameworks' in choices;

    // Se frameworks não existir no objeto, assumir que não é necessário
    // Se existir, precisa ter itens (hasFrameworks)
    // Sempre precisa de branch_choices
    this.choicesLoaded =
      hasBranchChoices && (frameworksExists ? hasFrameworks : true);

    // Só desativar loading quando choices, APIs e legacy version estiverem carregados
    if (
      this.choicesLoaded &&
      this.apisLoaded &&
      this.legacyVersionChecked &&
      this.loadingContent
    ) {
      this.loadingContent = false;
    }
  }

  async getEmployeeOptions() {
    try {
      const routeId = this.route.snapshot.paramMap.get('id');
      if (!routeId) {
        return;
      }
      const res = await this.api.get({
        route: 'api/registration/company/general/',
        params: { register_uuid: routeId },
        token: true,
      });
    } catch (error) {
      console.error('Erro em getEmployeeOptions:', error);
    }
  }

  /**
   * Carrega beneficiários de fundo exclusivo da API v3.
   * Se data (company/general) tiver beneficiário legado e a lista vier vazia, adiciona um.
   */
  async loadExclusiveFundBeneficiaries(
    registerUuid: string,
    legacyData?: {
      exclusive_fund?: boolean;
      beneficiary_name?: string;
      beneficiary_document?: string;
      beneficiary_is_pep?: boolean;
    },
  ): Promise<void> {
    try {
      const res = await this.api.get<{
        exclusive_fund_beneficiaries: ExclusiveFundBeneficiary[];
      }>({
        route: `api/v3/registration/registers/${registerUuid}/exclusive-fund-beneficiaries/`,
        token: true,
      });
      const list = res?.exclusive_fund_beneficiaries ?? [];
      this.exclusiveFundBeneficiaries = Array.isArray(list) ? [...list] : [];

      if (
        this.exclusiveFundBeneficiaries.length === 0 &&
        legacyData?.exclusive_fund &&
        legacyData?.beneficiary_name
      ) {
        this.exclusiveFundBeneficiaries.push({
          name: legacyData.beneficiary_name,
          document: legacyData.beneficiary_document
            ? String(legacyData.beneficiary_document)
                .replace(/\D/g, '')
                .trim() || String(legacyData.beneficiary_document)
            : '',
          is_pep: !!legacyData.beneficiary_is_pep,
        });
      }

      this.form.controls.exclusive_fund.setValue(
        this.exclusiveFundBeneficiaries.length > 0,
        { emitEvent: false },
      );
      this.initialExclusiveFundBeneficiaries = JSON.stringify(
        this.exclusiveFundBeneficiaries,
      );
      const exclusiveFundSelected = !!this.form.controls.exclusive_fund.value;
      if (!exclusiveFundSelected) {
        this.showBeneficiaryForm = false;
        this.disableBeneficiaryValidators();
      } else {
        // Fundo exclusivo marcado: só exige beneficiário se ainda não houver nenhum cadastrado
        this.showBeneficiaryForm = this.exclusiveFundBeneficiaries.length === 0;
        if (this.showBeneficiaryForm) {
          this.enableBeneficiaryValidators();
        } else {
          this.disableBeneficiaryValidators();
        }
      }
    } catch (err) {
      if (err?.status !== 404) {
        console.error(err);
      }
      // Fallback em caso de erro (ex.: 403): não exigir beneficiário e deixar checkbox desmarcado
      this.exclusiveFundBeneficiaries = [];
      if (legacyData?.exclusive_fund && legacyData?.beneficiary_name) {
        this.exclusiveFundBeneficiaries.push({
          name: legacyData.beneficiary_name,
          document: legacyData.beneficiary_document
            ? String(legacyData.beneficiary_document)
                .replace(/\D/g, '')
                .trim() || String(legacyData.beneficiary_document)
            : '',
          is_pep: !!legacyData.beneficiary_is_pep,
        });
      }
      this.form.controls.exclusive_fund.setValue(
        this.exclusiveFundBeneficiaries.length > 0,
        { emitEvent: false },
      );
      this.initialExclusiveFundBeneficiaries = JSON.stringify(
        this.exclusiveFundBeneficiaries,
      );
      const exclusiveFundSelected = !!this.form.controls.exclusive_fund.value;
      if (!exclusiveFundSelected) {
        this.showBeneficiaryForm = false;
        this.disableBeneficiaryValidators();
      } else {
        // Mesmo em fallback, se marked e sem beneficiários, exigir cadastro de ao menos um
        this.showBeneficiaryForm = this.exclusiveFundBeneficiaries.length === 0;
        if (this.showBeneficiaryForm) {
          this.enableBeneficiaryValidators();
        } else {
          this.disableBeneficiaryValidators();
        }
      }
    }
  }

  addBeneficiary(): void {
    const name = this.normalizeSpaces(
      this.form.controls.beneficiary_name.value,
    );
    const rawDoc = this.form.controls.beneficiary_document.value;
    const isEstrangeiro = this.form.controls.beneficiary_estrangeiro.value;
    const document = rawDoc
      ? isEstrangeiro
        ? String(rawDoc).trim()
        : Utils.normalizeDocumentForApi(String(rawDoc))
      : '';
    const is_pep = !!this.form.controls.beneficiary_is_pep.value;

    if (!name) {
      this.form.controls.beneficiary_name.markAsTouched();
      this.toast.show(
        'error',
        'Erro',
        'Informe o nome ou razão social do beneficiário.',
      );
      return;
    }
    if (!document) {
      this.form.controls.beneficiary_document.markAsTouched();
      this.toast.show(
        'error',
        'Erro',
        'Informe o documento (CPF/CNPJ ou documento do estrangeiro).',
      );
      return;
    }
    if (!isEstrangeiro && document.length !== 11 && document.length !== 14) {
      this.form.controls.beneficiary_document.setErrors({ invalid: true });
      this.form.controls.beneficiary_document.markAsTouched();
      this.toast.show(
        'error',
        'Erro',
        'CPF deve ter 11 dígitos e CNPJ 14 dígitos.',
      );
      return;
    }

    const alreadyExists = this.exclusiveFundBeneficiaries.some(
      (item) => item.document === document,
    );

    if (alreadyExists) {
      this.form.controls.beneficiary_document.setErrors({
        alreadyExists: true,
      });
      this.form.controls.beneficiary_document.markAsTouched();
      this.toast.show(
        'error',
        'Erro',
        'Já existe um beneficiário cadastrado com esse documento.',
      );
      return;
    }

    this.exclusiveFundBeneficiaries.push({ name, document, is_pep });
    this.clearBeneficiaryForm();
    this.form.controls.exclusive_fund.setValue(true, { emitEvent: false });
    this.showBeneficiaryForm = false;
    this.disableBeneficiaryValidators();
    this.updateFormStatus();
  }

  openBeneficiaryForm(): void {
    this.showBeneficiaryForm = true;
    this.enableBeneficiaryValidators();
  }

  removeBeneficiary(index: number): void {
    this.exclusiveFundBeneficiaries.splice(index, 1);
    if (
      this.exclusiveFundBeneficiaries.length === 0 &&
      this.form.controls.exclusive_fund.value
    ) {
      this.showBeneficiaryForm = true;
      this.enableBeneficiaryValidators();
    }
    this.updateFormStatus();
  }

  private clearBeneficiaryForm(): void {
    this.form.controls.beneficiary_name.setValue(null);
    this.form.controls.beneficiary_document.setValue(null);
    this.form.controls.beneficiary_estrangeiro.setValue(false, {
      emitEvent: false,
    });
    this.form.controls.beneficiary_is_pep.setValue(false, { emitEvent: false });
    this.form.controls.beneficiary_name.updateValueAndValidity();
    this.form.controls.beneficiary_document.updateValueAndValidity();
  }

  cancelBeneficiaryForm(): void {
    this.showBeneficiaryForm = false;
    this.clearBeneficiaryForm();
    this.disableBeneficiaryValidators();
    this.updateFormStatus();
  }

  verifyPepRelationshipFields() {
    if (this.form.controls.pep_relationship.value) {
      this.form.controls.pep_relationship_document.setValidators([
        Validators.required,
        UtilsValidators.cpf,
      ]);
      this.form.controls.pep_relationship_name.setValidators([
        Validators.required,
      ]);
      this.form.controls.pep_bond.setValidators([Validators.required]);
    } else {
      this.form.controls.pep_relationship_document.setValidators([]);
      this.form.controls.pep_relationship_name.setValidators([]);
      this.form.controls.pep_bond.setValidators([]);
    }

    this.form.controls.pep_relationship_document.updateValueAndValidity();
    this.form.controls.pep_relationship_name.updateValueAndValidity();
    this.form.controls.pep_bond.updateValueAndValidity();
  }

  setFieldsByRole() {
    if (this.authService.user) {
      /**
       * A PARTIR DE AGORA:
       * - NÃO usamos mais o active_register para descobrir se é PJ-FI.
       * - A fonte de verdade é sempre `signupService.getIsFund()`,
       *   que é alimentado apenas pelo componente pai via `required-files`.
       */
      const isFund = this.signupService.getIsFund();

      if (isFund) {
        this.form.controls.yearly_income.setValidators([]);
        this.form.controls.company_size.setValidators([]);
        this.form.controls.capital.setValidators([]);
        this.form.controls.giin.setValidators([Validators.required]);
        this.form.controls.cnae.setValidators([]);
        this.form.controls.cellphone_number.setValidators([
          Validators.required,
          Utils.validateWhenNotEmpty(UtilsValidators.celular),
        ]);
        this.form.controls.telephone_number.setValidators([
          Validators.required,
          Utils.validateWhenNotEmpty(UtilsValidators.telefoneFixo),
        ]);
        this.form.controls.contact_email.setValidators([]);
        this.form.controls.secondary_email.setValidators([
          Validators.required,
          Validators.email,
          UtilsValidators.emailValidator,
        ]);
        this.form.controls.authorization_bacen_queries.setValidators([
          Validators.requiredTrue,
        ]);
      } else {
        if (this.userRole !== 'provider') {
          this.form.controls.yearly_income.setValidators([
            Validators.required,
            Validators.min(1),
          ]);
          this.form.controls.capital.setValidators([
            Validators.required,
            Validators.min(1),
          ]);

          this.form.controls.authorization_bacen_queries.setValidators([
            Validators.requiredTrue,
          ]);

          this.form.controls.judicial_recovery_date.valueChanges
            .pipe(distinctUntilChanged((a: any, b: any) => a === b))
            .subscribe((value) => {
              if (!value) {
                this.form.controls.judicial_recovery_date.setValidators([]);
              } else {
                this.form.controls.judicial_recovery_date.setValidators([
                  Validators.required,
                  Utils.validateMaxMinToday,
                  Utils.validateMinDate,
                ]);
              }
            });

          this.form.controls.relationship_with_other_institutions.valueChanges.subscribe(
            (value) => {
              if (value) {
                this.form.controls.relationship_institution.setValidators([
                  Validators.required,
                ]);
              } else {
                this.form.controls.relationship_institution.setValidators([]);
              }
            },
          );

          this.form.controls.cellphone_number.updateValueAndValidity();
          this.form.controls.telephone_number.updateValueAndValidity();
          this.form.controls.relationship_institution.updateValueAndValidity();
          this.form.controls.cnae.updateValueAndValidity();
          this.form.controls.judicial_recovery_date.updateValueAndValidity();
          this.form.controls.yearly_income.updateValueAndValidity();
          this.form.controls.capital.updateValueAndValidity();
          this.form.controls.authorization_bacen_queries.updateValueAndValidity();
        }

        if (this.userRole === 'provider') {
          this.form.controls.number_employees.setValidators([
            Validators.required,
          ]);

          this.form.controls.cellphone_number.setValidators([
            Validators.required,
            Utils.validateWhenNotEmpty(UtilsValidators.celular),
          ]);
          this.form.controls.telephone_number.setValidators([
            Validators.required,
            Utils.validateWhenNotEmpty(UtilsValidators.telefoneFixo),
          ]);
        }
        this.form.controls.company_size.setValidators([Validators.required]);

        this.form.controls.giin.setValidators([]);
        this.form.controls.secondary_email.setValidators([
          Validators.email,
          UtilsValidators.emailValidator,
        ]);
      }

      if (
        this.authService.user.active_register.register.role.slug ===
          'ASSIGNOR-PJ-IF' ||
        this.authService.user.active_register.register.role.slug ===
          'GUARANTOR-PJ-IF'
      ) {
        this.form.controls.giin.setValidators([Validators.required]);
        this.form.controls.secondary_email.setValidators([
          Validators.required,
          Validators.email,
          UtilsValidators.emailValidator,
        ]);
        // Não atualizar o serviço aqui - apenas o componente principal deve fazer isso
      }

      this.form.controls.number_employees.updateValueAndValidity();
      this.form.controls.yearly_income.updateValueAndValidity();
      this.form.controls.company_size.updateValueAndValidity();
      this.form.controls.capital.updateValueAndValidity();
      this.form.controls.giin.updateValueAndValidity();
      this.form.controls.secondary_email.updateValueAndValidity();
      this.form.controls.authorization_bacen_queries.updateValueAndValidity();
    }
  }

  thirdPartyFields() {
    if (this.form.controls.third_party.value) {
      this.form.controls.third_party_document.setValidators([
        Validators.required,
        Utils.validateCpfCnpj,
      ]);
      this.form.controls.third_party_name.setValidators([Validators.required]);
    } else {
      this.form.controls.third_party_document.setValidators([]);
      this.form.controls.third_party_name.setValidators([]);
    }

    this.form.controls.third_party_document.updateValueAndValidity();
    this.form.controls.third_party_name.updateValueAndValidity();
  }

  async getDataAndFillForm() {
    try {
      const routeId = this.route.snapshot.paramMap.get('id');
      if (!routeId) {
        this.toast.show(
          'error',
          'Erro',
          'Register UUID não encontrado na rota',
        );
        return;
      }

      const { data } = await this.api.get<ApiResponse<Company>>({
        route: 'api/registration/company/general/',
        params: { register_uuid: routeId },
        token: true,
      });

      // Se data é null, significa que ainda não tem dados cadastrados (normal)
      if (!data || data === null) {
        if (this.isFund && this.userRole === 'shareholder') {
          await this.loadExclusiveFundBeneficiaries(routeId);
        }
        return;
      }

      if (!data.document || !data.document.number) {
        return;
      }

      this.dataDocument = data.document.number;
      // Criar parsedData apenas com os campos que existem no formulário
      // NÃO usar ...data para evitar incluir campos desnecessários da API

      // Verificações de segurança para propriedades aninhadas
      // DDI sempre deve ser "55" (Brasil) por padrão
      // Se o código for um código de área (2 dígitos como 11, 21, etc), usar "55" como DDI
      const cellphoneCode = data.cellphone?.code
        ? String(data.cellphone.code).length <= 2
          ? '55'
          : String(data.cellphone.code)
        : '55';
      const cellphoneNumber = data.cellphone?.number
        ? this.maskPipe.transform(data.cellphone.number, '(00) 00000-0000')
        : '';
      // DDI sempre deve ser "55" (Brasil) por padrão
      // Se o código for um código de área (2 dígitos como 11, 21, etc), usar "55" como DDI
      const phoneCode = data.phone?.code
        ? String(data.phone.code).length <= 2
          ? '55'
          : String(data.phone.code)
        : '55';
      const phoneNumber = data.phone?.number
        ? this.maskPipe.transform(
            data.phone.number,
            data.phone.number.length === 10
              ? '(00) 0000-0000'
              : '(00) 00000-0000',
          )
        : '';
      const relationshipInstitution =
        data.assignor?.relationship_institution || '';

      const parsedData: any = {
        cnpj: Utils.formatDocumentForDisplay(data.document.number),
        foundation_date: data.foundation_date
          ? data.foundation_date.split('-').reverse().join('/')
          : null,
        name: data.corporate_name || null,
        brand_name: data.brand_name || null,
        municipal_registration: data.municipal_registration || null,
        state_registration: data.state_registration || null,
        free_state_registration: data.free_state_registration || false,
        // joint_obligation: data.assignor
        //   ? data.assignor.joint_obligation === 'true'
        //   : false,
        cellphone_ddi: cellphoneCode,
        cellphone_number: cellphoneNumber,
        telephone_ddi: phoneCode,
        telephone_number: phoneNumber,
        send_info: true,
        judicial_recovery: data.judicial_recovery || false,
        have_judicial_recovery: data.have_judicial_recovery || false,
        judicial_recovery_date: data.judicial_recovery_date
          ? data.judicial_recovery_date.split('-').reverse().join('/')
          : null,
        pep_relationship: false,
        pep_relationship_document: '',
        pep_relationship_name: '',
        pep_bond: '',
        yearly_income: Number(data.yearly_income) || 0,

        authorization_bacen_queries:
          data.assignor?.authorization_bacen_queries ?? false,
        sfn: (data as any).sfn ?? false,
        relationship_with_other_institutions:
          data.assignor?.relationship_with_other_institutions ?? false,
        relationship_institution: relationshipInstitution,
        third_party: false,
        third_party_document: '',
        third_party_name: '',
        contract_type: data.contract_type ? String(data.contract_type) : '',

        branch: data.branch ? String(data.branch) : null,
        framework: data.framework ? String(data.framework) : null,
        cnae: data.cnae ? String(data.cnae) : null,
        contact_email: data.contact_email ? String(data.contact_email) : '',
        capital: Number(data.capital) || 0,
        company_size: data.company_size ? String(data.company_size) : null,
        number_employees: this.handleGetEmployees(data.number_employees),
        nire: data.nire || null,
        giin: data.giin || null,
        agent: (data as any).agent || null,
        billet_emitter: data.billet_emitter || false,
        nfe_emitter: data.nfe_emitter || false,
        duplicate_emitter: data.duplicate_emitter || false,
        site: data.site || '',
        secondary_email: null,
        primary_contact: false,
        exclusive_fund: data.exclusive_fund || false,
        beneficiary_name: null,
        beneficiary_document: null,
        beneficiary_estrangeiro: false,
        beneficiary_is_pep: false,
      };

      // Filtrar apenas campos que existem no form antes de fazer patchValue
      const formControlKeys = Object.keys(this.form.controls);
      const filteredParsedData: any = {};

      Object.keys(parsedData).forEach((key) => {
        if (formControlKeys.includes(key)) {
          filteredParsedData[key] = parsedData[key];
        }
      });

      this.form.patchValue(filteredParsedData);

      if (this.isFund && this.userRole === 'shareholder') {
        await this.loadExclusiveFundBeneficiaries(routeId, {
          exclusive_fund: data.exclusive_fund,
          beneficiary_name: data.beneficiary_name,
          beneficiary_document: data.beneficiary_document,
          beneficiary_is_pep: data.beneficiary_is_pep,
        });
      }

      this.applySiteValidatorsByFund();

      if (data.assignor?.relationship_institution) {
        if (this.form.controls.relationship_institution) {
          this.form.controls.relationship_institution.setValidators([
            Validators.required,
          ]);
          this.form.controls.relationship_institution.updateValueAndValidity();
        }
      }

      if (this.form.controls.secondary_email) {
        this.form.controls.secondary_email.setValue(null);
      }

      if (data.contact_email && data.contact_email !== ' ') {
        const secondaryEmails = Array.isArray(data.secondary_email)
          ? data.secondary_email.map((item: any) => {
              return {
                label: item,
                primary: false,
              };
            })
          : [];

        this.emailArr = [
          {
            label: data.contact_email,
            primary: true,
          },
          ...secondaryEmails,
        ];
      }

      this.updateSecondaryEmailValidation();
      this.data = parsedData;
      this.initialPayload = this.buildPayloadFromForm();

      // Forçar atualização do status do formulário após carregar dados
      // Aguardar um pouco para garantir que todos os valores foram aplicados
      setTimeout(() => {
        // Forçar validação de todos os campos
        Object.keys(this.form.controls).forEach((key) => {
          this.form.controls[key].updateValueAndValidity({ emitEvent: false });
          // Marcar como touched se houver erro para mostrar mensagem de erro
          if (
            this.form.controls[key].invalid &&
            this.form.controls[key].value
          ) {
            this.form.controls[key].markAsTouched();
          }
        });
        this.updateFormStatus();
      }, 100);
    } catch (error) {
      this.initialPayload = null;
      console.error(error);
      if (error.status !== 404) {
        this.toast.show(
          'error',
          'Erro',
          error.error?.message || 'Erro ao carregar dados',
        );
      }
    }
  }

  handleGetEmployees(value: any) {
    if (value === null) return null;

    const validValue = this.employeesOptions.find(
      (item) => item.value === value,
    );

    if (validValue) return value;

    const newValue = Number(value);

    if (newValue >= 1 && newValue <= 10) return 'one_to_ten';
    if (newValue >= 11 && newValue <= 100) return 'eleven_to_fifty';
    if (newValue >= 101 && newValue <= 500) return 'fifty_one_to_two_hundred';
    if (newValue >= 501) return 'two_hundred_one_to_five_hundred';
  }

  private applySiteValidatorsByFund() {
    if (this.signupService.getIsFund()) {
      this.form.controls.site.setValidators([]);
    } else {
      this.form.controls.site.setValidators([
        UtilsValidators.notOnlyWhitespace,
        UtilsValidators.noSpaces,
        UtilsValidators.urlValidator,
      ]);
    }
    this.form.controls.site.updateValueAndValidity({ emitEvent: false });
  }

  private applyBeneficiaryDocumentValidators() {
    const isEstrangeiro = this.form.controls.beneficiary_estrangeiro.value;
    if (isEstrangeiro) {
      this.form.controls.beneficiary_document.setValidators([
        Validators.required,
      ]);
    } else {
      this.form.controls.beneficiary_document.setValidators([
        Validators.required,
        Utils.validateCpfCnpj,
      ]);
    }
  }

  private enableBeneficiaryValidators() {
    this.form.controls.beneficiary_name.setValidators([
      Validators.required,
      UtilsValidators.notOnlyWhitespace,
    ]);
    this.applyBeneficiaryDocumentValidators();
    this.form.controls.beneficiary_name.updateValueAndValidity({
      emitEvent: false,
    });
    this.form.controls.beneficiary_document.updateValueAndValidity({
      emitEvent: false,
    });
  }

  private disableBeneficiaryValidators() {
    this.form.controls.beneficiary_name.setValidators([]);
    this.form.controls.beneficiary_document.setValidators([]);
    this.form.controls.beneficiary_name.updateValueAndValidity({
      emitEvent: false,
    });
    this.form.controls.beneficiary_document.updateValueAndValidity({
      emitEvent: false,
    });
  }

  // Função auxiliar para normalizar espaços (remove espaços duplos e faz trim)
  private normalizeSpaces(value: string | null | undefined): string | null {
    if (!value || typeof value !== 'string') {
      return value || null;
    }
    return value.trim().replace(/\s+/g, ' ') || null;
  }

  // Função auxiliar para remover todos os espaços (usado para campos como site)
  private removeAllSpaces(value: string | null | undefined): string | null {
    if (!value || typeof value !== 'string') {
      return value || null;
    }
    return value.replace(/\s/g, '') || null;
  }

  // Função auxiliar para normalizar email (converte para minúsculas e faz trim)
  private normalizeEmail(value: string | null | undefined): string | null {
    if (!value || typeof value !== 'string') {
      return value || null;
    }
    return value.trim().toLowerCase() || null;
  }

  private buildPayloadFromForm(): Record<string, any> {
    const valuesHelper = new GetHelper(this.form.value);
    const fundEmail = this.emailArr.find((item) => item.primary);
    const secondaryEmails = this.emailArr
      .filter((item) => !item.primary)
      .map((item) => this.normalizeEmail(item.label))
      .filter((email) => email !== null);

    const payload: Record<string, any> = {
      document: {
        number: Utils.normalizeDocumentForApi(valuesHelper.get('cnpj')),
        type: 'CNPJ',
      },
      corporate_name: this.normalizeSpaces(valuesHelper.get('name')),
      foundation_date: valuesHelper.get('foundation_date')
        ? valuesHelper.get('foundation_date').split('/').reverse().join('-')
        : null,
      role: 1,
      giin: this.normalizeSpaces(valuesHelper.get('giin')),
      nire: this.normalizeSpaces(valuesHelper.get('nire')),
      contact_email: this.signupService.getIsFund()
        ? this.normalizeEmail(fundEmail?.label)
        : this.normalizeEmail(valuesHelper.get('contact_email')),
      site: this.removeAllSpaces(valuesHelper.get('site')),
      send_info: valuesHelper.get('send_info'),
      cnae: valuesHelper.get('cnae') ? Number(valuesHelper.get('cnae')) : null,
      number_employees: valuesHelper.get('number_employees'),
      yearly_income: valuesHelper.get('yearly_income')
        ? Number(valuesHelper.get('yearly_income'))
        : 0,
      judicial_recovery: valuesHelper.get('judicial_recovery'),
      judicial_recovery_date: valuesHelper.get('judicial_recovery_date')
        ? valuesHelper
            .get('judicial_recovery_date')
            .split('/')
            .reverse()
            .join('-')
        : null,
      branch: valuesHelper.get('branch'),
      capital: Number(valuesHelper.get('capital')),
      framework: valuesHelper.get('framework'),
      municipal_registration: this.normalizeSpaces(
        valuesHelper.get('municipal_registration'),
      ),
      company_size: valuesHelper.get('company_size'),
      state_registration: this.normalizeSpaces(
        valuesHelper.get('state_registration'),
      ),
      free_state_registration: valuesHelper.get('free_state_registration'),
      billet_emitter: valuesHelper.get('billet_emitter'),
      nfe_emitter: valuesHelper.get('nfe_emitter'),
      duplicate_emitter: valuesHelper.get('duplicate_emitter'),
      have_judicial_recovery: valuesHelper.get('have_judicial_recovery'),
      contract_type: valuesHelper.get('contract_type'),
      brand_name: this.normalizeSpaces(valuesHelper.get('brand_name')),
      secondary_email: secondaryEmails ?? null,
      exclusive_fund: !!valuesHelper.get('exclusive_fund'),
    };

    const telNumber = valuesHelper.get('telephone_number');
    if (telNumber !== '' && telNumber !== null) {
      payload['phone'] = {
        type: 'residential',
        code: Number(valuesHelper.get('telephone_ddi') || 55),
        number: Utils.onlyNumbers(telNumber),
        branch: 0,
      };
    } else {
      payload['phone'] = null;
    }

    const cellNumber = valuesHelper.get('cellphone_number');
    if (cellNumber !== '' && cellNumber !== null) {
      payload['cellphone'] = {
        type: 'phone',
        code: Number(valuesHelper.get('cellphone_ddi') || 55),
        number: Utils.onlyNumbers(cellNumber),
        branch: 0,
      };
    } else {
      payload['cellphone'] = null;
    }

    // Inclui `assignor` no cadastro unificado mesmo quando `applicable` no auth
    // ainda não está como assignor (ex.: cedente com userRole === 'assignor').
    const includeCompanyAssignorBlock =
      this.authService.user?.active_register?.register?.role?.applicable ===
        'assignor' ||
      this.userRole === 'shareholder' ||
      this.userRole === 'assignor';

    if (includeCompanyAssignorBlock) {
      payload['assignor'] = {
        authorization_bacen_queries: valuesHelper.get(
          'authorization_bacen_queries',
        ),
        relationship_with_other_institutions: valuesHelper.get(
          'relationship_with_other_institutions',
        ),
        relationship_institution: this.normalizeSpaces(
          valuesHelper.get('relationship_institution'),
        ),
      };
    }

    // Mesmo critério do template (*ngIf="userRole === 'assignor'"): SFN na raiz do body.
    if (this.userRole === 'assignor') {
      payload['sfn'] = !!this.form.get('sfn')?.value;
    }

    if (this.userRole === 'provider') {
      payload['agent'] = valuesHelper.get('agent');
    }

    return payload;
  }

  async sendData() {
    const routeId = this.route.snapshot.paramMap.get('id');
    if (!routeId) {
      this.toast.show('error', 'Erro', 'Register UUID não encontrado na rota');
      return;
    }

    const payload = this.buildPayloadFromForm();
    const exclusiveFundSelected = !!this.form.controls.exclusive_fund.value;

    let initialBeneficiaries: ExclusiveFundBeneficiary[] = [];
    try {
      initialBeneficiaries = this.initialExclusiveFundBeneficiaries
        ? JSON.parse(this.initialExclusiveFundBeneficiaries)
        : [];
    } catch {
      initialBeneficiaries = [];
    }

    const beneficiariesChanged =
      this.isFund &&
      this.userRole === 'shareholder' &&
      exclusiveFundSelected &&
      JSON.stringify(this.exclusiveFundBeneficiaries) !==
        this.initialExclusiveFundBeneficiaries;

    // Caso em que existiam beneficiários salvos e o usuário desmarcou o fundo exclusivo:
    // precisamos enviar um DELETE para a API v3.
    const hadInitialBeneficiaries =
      Array.isArray(initialBeneficiaries) && initialBeneficiaries.length > 0;
    const exclusiveFundDeselected =
      this.isFund &&
      this.userRole === 'shareholder' &&
      !exclusiveFundSelected &&
      hadInitialBeneficiaries;

    if (
      payloadsEqual(payload, this.initialPayload ?? undefined) &&
      !beneficiariesChanged &&
      !exclusiveFundDeselected
    ) {
      this.signupService.loading = false;
      this.signupService.setComplementaryCompanyStep('complementaryAddress');
      this.signupService.changeCompanyStepEvent.emit('address');
      return;
    }

    this.signupService.loading = true;

    try {
      const payloadChanged = !payloadsEqual(
        payload,
        this.initialPayload ?? undefined,
      );
      if (payloadChanged) {
        if (this.data) {
          await this.api.put<ApiResponse<Company>>({
            route: 'api/registration/company/general/',
            params: { register_uuid: routeId },
            token: true,
            body: payload,
          });
        } else {
          await this.api.post<ApiResponse<Company>>({
            route: 'api/registration/company/general/',
            params: { register_uuid: routeId },
            token: true,
            body: payload,
          });
        }
      }

      if (this.isFund && this.userRole === 'shareholder') {
        const beneficiariesRoute = `api/v3/registration/registers/${routeId}/exclusive-fund-beneficiaries/`;

        // Se o fundo exclusivo permanece marcado e a lista mudou → PATCH/DELETE normal.
        if (exclusiveFundSelected && beneficiariesChanged) {
          if (this.exclusiveFundBeneficiaries.length === 0) {
            await this.api.delete({
              route: beneficiariesRoute,
              token: true,
            });
          } else {
            await this.api.patch({
              route: beneficiariesRoute,
              token: true,
              body: {
                exclusive_fund_beneficiaries: this.exclusiveFundBeneficiaries,
              },
            });
          }
        }

        // Se o usuário desmarcou o fundo exclusivo e havia beneficiários antes → DELETE.
        if (!exclusiveFundSelected && exclusiveFundDeselected) {
          await this.api.delete({
            route: beneficiariesRoute,
            token: true,
          });
          this.exclusiveFundBeneficiaries = [];
        }
      }

      this.initialPayload = this.buildPayloadFromForm();
      this.initialExclusiveFundBeneficiaries = JSON.stringify(
        this.exclusiveFundBeneficiaries,
      );
      this.toast.show('info', 'Sucesso', 'Dados salvos com sucesso!');

      this.signupService.changeBasicInfoEvent.emit(payload.corporate_name);

      this.signupService.setComplementaryCompanyStep('complementaryAddress');
      this.signupService.changeCompanyStepEvent.emit('address');
    } catch (error) {
      const errorMessage =
        error?.error?.data ||
        error?.error?.message ||
        error?.message ||
        'Erro ao salvar dados';
      this.toast.show('error', 'Erro', errorMessage);

      if (error?.error?.data && String(error.error.data).includes('nire')) {
        this.form.controls.nire.setErrors({ invalid: true });
      }
      this.signupService.loading = false;
    } finally {
      this.signupService.loading = false;
    }
  }

  removeEmail(index: number) {
    this.emailArr.splice(index, 1);

    const primaryEmail = this.emailArr.find((item) => item.primary);

    if (!primaryEmail && this.emailArr.length > 0) {
      this.emailArr[0].primary = true;
    }

    this.updateSecondaryEmailValidation();
    this.updateFormStatus();
  }

  async getFunds() {
    try {
      const res = await this.api.get({
        route: 'api/registration/fund',
        token: false,
      });

      const filteredArr = Utils.filterRepeatedValues(res.funds, 'id');

      const funds = filteredArr.map((fund: any) => {
        return {
          label: fund.name,
          value: fund.id,
        };
      });

      this.fundOptions = funds;
    } catch (error) {
      this.toast.show('error', 'Erro!', 'Erro ao carregar fundos de interesse');
    }
  }

  addEmail() {
    const alreadyHaveEmail = this.emailArr.some(
      (item) => item.label === this.form.controls.secondary_email.value,
    );

    if (alreadyHaveEmail) {
      this.toast.show('error', 'Erro!', 'Email já cadastrado');
      this.clearEmailGroup();
      return;
    }

    const alreadyHavePrimary = this.emailArr.some((item) => item.primary);

    if (alreadyHavePrimary && this.form.controls.primary_contact.value) {
      const index = this.emailArr.findIndex((item) => item.primary);

      this.emailArr[index].primary = false;

      this.emailArr.push({
        label: this.form.controls.secondary_email.value,
        primary: true,
      });
    } else {
      this.emailArr.push({
        label: this.form.controls.secondary_email.value,
        primary:
          this.emailArr.length === 0 ||
          this.form.controls.primary_contact.value,
      });
    }

    this.clearEmailGroup();
    this.updateSecondaryEmailValidation();
    this.updateFormStatus();
  }

  async checkDocument(number: string, type: string) {
    try {
      const res = await this.api.post({
        route: 'api/registration/check_fund/',
        token: true,
        body: { number: number, type: type },
      });

      if (res) {
        this.form.controls.cnpj.setErrors({ alreadyExists: false });
      }
    } catch (error) {
      this.form.controls.cnpj.setErrors({ alreadyExists: true });
      this.toast.show('error', 'Erro!', 'CNPJ já cadastrado');
    }
  }

  clearEmailGroup() {
    this.form.controls.secondary_email.setValue(null);
    this.form.controls.primary_contact.setValue(false);
    this.form.controls.secondary_email.updateValueAndValidity();
  }

  updateSecondaryEmailValidation() {
    if (this.signupService.getIsFund()) {
      if (!this.form.controls.secondary_email) {
        return;
      }

      if (this.emailArr.length > 0) {
        // Se já tem emails no array, o campo não precisa ser obrigatório
        this.form.controls.secondary_email.setValidators([
          Validators.email,
          UtilsValidators.emailValidator,
        ]);
      } else {
        // Se não tem emails no array, o campo precisa ser obrigatório
        this.form.controls.secondary_email.setValidators([
          Validators.required,
          Validators.email,
          UtilsValidators.emailValidator,
        ]);
      }
      this.form.controls.secondary_email.updateValueAndValidity();

      this.updateFormStatus();
    }
  }

  isFormValid(): boolean {
    try {
      const formValid = this.form.valid;
      const isFund = this.signupService.getIsFund();

      // Se for fundo de investimento, precisa ter pelo menos um email no array
      if (isFund && this.emailArr.length === 0) {
        return false;
      }

      // Se for fundo exclusivo (PJ-FI shareholder) marcado, precisa ter pelo menos um beneficiário
      if (
        isFund &&
        this.userRole === 'shareholder' &&
        this.form.controls.exclusive_fund.value &&
        this.exclusiveFundBeneficiaries.length === 0
      ) {
        return false;
      }

      // Se o formulário de beneficiário estiver aberto, bloquear o "Seguir"
      if (
        isFund &&
        this.userRole === 'shareholder' &&
        this.form.controls.exclusive_fund.value &&
        this.showBeneficiaryForm
      ) {
        return false;
      }

      return formValid;
    } catch (error) {
      return false;
    }
  }

  updateFormStatus() {
    try {
      const isValid = this.isFormValid();
      this.signupService.setCompanyFormStatus('complementaryGeneral', isValid);
    } catch (error) {
      // Erro silencioso - não afeta o fluxo
    }
  }

  async getAgents() {
    try {
      const res = await this.api.get({
        route: 'api/registration/simplified-agent/',
      });

      this.agentOptions = res.map((item: any) => {
        return {
          label: item.name,
          value: item.id,
        };
      });

      const newUserDataStorage = this.storageService.getNewUserData();

      if (newUserDataStorage) {
        if (this.userRole === 'provider') {
          this.form.patchValue({
            agent: newUserDataStorage.role,
          });
        }
      }
    } catch (error) {
      console.error('Erro em getAgents:', error);
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao carregar os dados.');
    }
  }

  /**
   * Método removido - não precisa mais chamar API legacy
   * O backend já cria versão 1 automaticamente quando não existe versão 1
   */
  private async checkAndCreateLegacyVersion(): Promise<void> {
    // Não precisa mais fazer nada - o backend já cria versão 1 automaticamente
    this.legacyVersionChecked = true;
    this.checkChoicesAndDisableLoading();
  }
}
